import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "reactstrap";
import { SwitchToggle } from "../../Components/TableHelper";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import Select from "react-select";
import { CheckSquare, Square } from "lucide-react";
import axiosInstance from "../../axiosInterceptor";
import { customStyles } from "../../utils/constant";
import { menuSchema } from "../../Validation/Validation";

const AddMenu = () => {
  const navigator = useNavigate();
  const location = useLocation();
  const editData = location.state;
  const [loading, setLoading] = useState(false);
  const editingId = editData?._id;
  const [categoryData, setCategoryData] = useState([]);

  useEffect(() => {
    axiosInstance
      .get("category/getWithoutPaginationCategory")
      .then((res) => setCategoryData(res.data?.data || []))
      .catch(() => setCategoryData([]));
  }, []);

  const initialValues = {
    sortOrder: editData?.sortOrder || 1,
    menuName: editData?.menuName || "",
    menuType: editData?.menuType || "",
    menuUrl: editData?.menuUrl || "",
    categoryId: editData?.categoryId || null,
    isActive: editData?.isActive ?? true,
    showInHeader: editData?.showInHeader ?? false,
    showInFooter: editData?.showInFooter ?? false,
    metaTitle: editData?.metaTitle || "",
    metaDescription: editData?.metaDescription || "",
    metaKeywords: editData?.metaKeywords || "",
  };

  const {
    values,
    errors,
    handleChange,
    handleSubmit,
    handleBlur,
    setFieldValue,
    touched,
  } = useFormik({
    initialValues,
    validationSchema: menuSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const response = editingId
          ? await axiosInstance.put(`/menu/updateMenu/${editingId}`, values)
          : await axiosInstance.post("/menu/createMenu", values);

        if (response.status === 200) {
          toast.success(response.data.message);
          navigator("/menu", {
            state: {
              currentPage: editData?.currentPage || 1,
              perPage: editData?.perPage || 10,
            },
          });
        }
      } catch (error) {
        toast.error(error.response?.data?.message || "Something went wrong.");
      } finally {
        setLoading(false);
      }
    },
  });

  const MenuType = [
    { label: "Home", value: "Home" },
    { label: "Category", value: "Category" },
    { label: "Contact", value: "Contact" },
    { label: "Products", value: "Products" },
    { label: "Faq", value: "Faq" },
  ];

  const categoryOptions = categoryData.map((item) => ({
    label: item.categoryName,
    value: item._id,
    categoryLink: item.categoryLink,
  }));

  const showMenuUrl =
    values.menuType === "Page" ||
    values.menuType === "Faq" ||
    values.menuType === "Contact" ||
    values.menuType === "Products" ||
    values.menuType === "Home";

  return (
    <>
      <div className="content-wrapper">
        <div className="page-header mb-3">
          <h3 className="custom-page-title">
            {editingId ? "Update" : "Add"} Menu
          </h3>
        </div>

        <div className="col-md-12 grid-margin stretch-card p-0">
          <div className="card">
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label
                        htmlFor="menu-sortOrder"
                        className="col-form-label"
                      >
                        Sort Order
                      </label>
                      <input
                        type="number"
                        className="form-control border-dark"
                        id="menu-sortOrder"
                        name="sortOrder"
                        placeholder="Enter Sort Order"
                        value={values.sortOrder}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      {touched.sortOrder && errors.sortOrder && (
                        <small className="text-danger">
                          {errors.sortOrder}
                        </small>
                      )}
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label htmlFor="menu-MenuType" className="col-form-label">
                        Select Menu Type
                      </label>
                      <Select
                        placeholder="Select Menu Type"
                        isClearable
                        styles={customStyles}
                        options={MenuType}
                        value={
                          MenuType.find(
                            (opt) => opt.value === values.menuType,
                          ) || null
                        }
                        onChange={(option) => {
                          setFieldValue("menuType", option ? option.value : "");
                        }}
                        name="menuType"
                      />
                      {touched.menuType && errors.menuType && (
                        <small className="text-danger">{errors.menuType}</small>
                      )}
                    </div>
                  </div>

                  {values.menuType === "Category" && (
                    <div className="col-sm-12">
                      <div className="form-group">
                        <label
                          htmlFor="menu-Categories"
                          className="col-form-label"
                        >
                          Select Category
                        </label>
                        <Select
                          placeholder="Select Categories"
                          styles={customStyles}
                          options={categoryOptions}
                          value={
                            categoryOptions.find(
                              (opt) => opt.value === values.categoryId,
                            ) || null
                          }
                          onChange={(option) => {
                            setFieldValue(
                              "categoryId",
                              option ? option.value : null,
                            );
                          }}
                          name="categoryId"
                        />
                        {touched.categoryId && errors.categoryId && (
                          <small className="text-danger">
                            {errors.categoryId}
                          </small>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="col-sm-6">
                    <div className="form-group">
                      <label htmlFor="menuName" className="col-form-label">
                        Menu Name
                      </label>
                      <input
                        type="text"
                        className="form-control border-dark"
                        id="menuName"
                        name="menuName"
                        placeholder="Enter Menu Name"
                        value={values.menuName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      {touched.menuName && errors.menuName && (
                        <small className="text-danger">{errors.menuName}</small>
                      )}
                    </div>
                  </div>

                  {showMenuUrl && (
                    <div className="col-sm-6">
                      <div className="form-group">
                        <label htmlFor="menuUrl" className="col-form-label">
                          Menu URL
                        </label>
                        <input
                          type="text"
                          className="form-control border-dark"
                          id="menuUrl"
                          name="menuUrl"
                          placeholder="Enter Menu URL"
                          value={values.menuUrl}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        {touched.menuUrl && errors.menuUrl && (
                          <small className="text-danger">
                            {errors.menuUrl}
                          </small>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Meta Title */}
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label htmlFor="metaTitle" className="col-form-label">
                        Meta Title
                      </label>
                      <input
                        type="text"
                        className="form-control border-dark"
                        id="metaTitle"
                        name="metaTitle"
                        placeholder="Enter Meta Title"
                        value={values.metaTitle}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      {touched.metaTitle && errors.metaTitle && (
                        <small className="text-danger">
                          {errors.metaTitle}
                        </small>
                      )}
                    </div>
                  </div>

                  {/* Meta Keywords */}
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label htmlFor="metaKeywords" className="col-form-label">
                        Meta Keywords
                      </label>
                      <textarea
                        className="form-control border-dark"
                        id="metaKeywords"
                        name="metaKeywords"
                        placeholder="Enter Meta Keywords"
                        rows={5}
                        style={{ resize: "vertical", minHeight: "45px" }}
                        value={values.metaKeywords}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      {touched.metaKeywords && errors.metaKeywords && (
                        <small className="text-danger">
                          {errors.metaKeywords}
                        </small>
                      )}
                    </div>
                  </div>

                  {/* Meta Description */}
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label
                        htmlFor="metaDescription"
                        className="col-form-label"
                      >
                        Meta Description
                      </label>
                      <textarea
                        className="form-control border-dark"
                        id="metaDescription"
                        name="metaDescription"
                        placeholder="Enter Meta Description"
                        rows={5}
                        style={{ resize: "vertical", minHeight: "45px" }}
                        value={values.metaDescription}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      {touched.metaDescription && errors.metaDescription && (
                        <small className="text-danger">
                          {errors.metaDescription}
                        </small>
                      )}
                    </div>
                  </div>

                  <div className="col-md-6 mt-4">
                    <SwitchToggle
                      checked={values.isActive}
                      onChange={(val) => setFieldValue("isActive", val)}
                    />
                  </div>

                  <div className="col-md-6 mt-4">
                    <div className="d-flex flex-column" style={{ gap: "10px" }}>
                      <div
                        className={`menu-row ${values.showInHeader ? "active" : ""}`}
                        onClick={() =>
                          setFieldValue("showInHeader", !values.showInHeader)
                        }
                        style={{ cursor: "pointer" }}
                      >
                        {values.showInHeader ? (
                          <CheckSquare size={20} className="icon-active" />
                        ) : (
                          <Square size={20} className="icon" />
                        )}
                        <span className="menu-title">Show In Header</span>
                      </div>

                      <div
                        className={`menu-row ${values.showInFooter ? "active" : ""}`}
                        onClick={() =>
                          setFieldValue("showInFooter", !values.showInFooter)
                        }
                        style={{ cursor: "pointer" }}
                      >
                        {values.showInFooter ? (
                          <CheckSquare size={20} className="icon-active" />
                        ) : (
                          <Square size={20} className="icon" />
                        )}
                        <span className="menu-title">Show In Footer</span>
                      </div>
                    </div>
                  </div>
                </div>
                {/* end row */}

                {/* Action buttons */}
                <div
                  className="text-start mt-4"
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "10px",
                  }}
                >
                  <Button type="submit" color="primary" disabled={loading}>
                    {loading ? (
                      <span className="d-inline-flex align-items-center gap-2">
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden="true"
                        />
                        Processing...
                      </span>
                    ) : (
                      "Save"
                    )}
                  </Button>

                  <Button
                    color="danger"
                    className="mx-2"
                    onClick={() =>
                      navigator("/menu", {
                        state: {
                          currentPage: editData?.currentPage || 1,
                          perPage: editData?.perPage || 10,
                          search: editData?.search || "",
                        },
                      })
                    }
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddMenu;
